import { useContext } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { isSupportAssistantEnabled } from 'selectors/featuresSelectors'
import config from 'constants/config'
import LineCommentsIcon from 'LuxKit/Icons/line/LineCommentsIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import GeoContext from 'contexts/geoContext'
import { useRenderWhatsApp } from 'hooks/useRenderWhatsApp'
import ContactContext from 'contexts/contactContext'
import { getSVGImageUrl } from 'lib/image/imageUtils'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import useInfiniteConciergeDetails from 'loyalty/hooks/useInfiniteConciergeDetails'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'

interface Props {
  supportAssistantEnabled: boolean
  className?: string
  onClick: () => void
}

function PhoneNumberMobileMenu({
  supportAssistantEnabled,
  onClick,
  className,
}: Props) {
  const intl = useIntl()
  const currentContact = useContext(ContactContext)
  const isInfiniteMember = useAppSelector(state => getLoyaltyIsInfiniteAccountTier(state))
  const conciergeDetails = useInfiniteConciergeDetails()
  const localContact = currentContact.local
  const internationalContact = currentContact.international
  const { currentRegionCode } = useContext(GeoContext)
  const shouldRenderWhatsApp = useRenderWhatsApp(currentRegionCode)
  const localContactTitle = intl.formatMessage({
    id: 'n-Zw7I5xyaAIUMs7lm0V',
    defaultMessage: 'Local Sales & Support',
    description: 'Title for local sales and support contact in mobile phone menu',
  })

  return (
    <div className={className}>
      {isInfiniteMember && conciergeDetails && (
        <ClickableListItem
          title={conciergeDetails.title}
          subtitle={conciergeDetails.phoneNumber}
          startIcon={<LinePhoneIcon />}
          href={conciergeDetails.phoneHref}
          aria-label={conciergeDetails.title}
          rel="nofollow"
        />
      )}
      {!NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode) && (
        <ClickableListItem
          title={localContactTitle}
          subtitle={localContact.humanReadable}
          startIcon={<LinePhoneIcon />}
          href={`tel:${localContact.number}`}
          aria-label={intl.formatMessage({
            id: 'LP65m9AA5Fa0BQZMJ4z7',
            defaultMessage: 'Local Sales & Support contact',
            description: 'Aria label for local sales and support contact in mobile phone menu',
          })}
          rel="nofollow"
        />
      )}
      {supportAssistantEnabled && <ClickableListItem
        title={intl.formatMessage({
          id: 'kmdmb91EdFlLj5hdPHAe',
          defaultMessage: 'Chat now',
          description: 'Title for chat support option in mobile phone menu',
        })}
        subtitle={intl.formatMessage({
          id: 'M4IF7X8Li48OPpnZ0SYX',
          defaultMessage: 'Luxury Escapes Support Assistant',
          description: 'Subtitle for chat support in mobile phone menu in the site header',
        })}
        startIcon={<LineCommentsIcon />}
        to="/support"
        onClick={onClick}
        aria-label={intl.formatMessage({
          id: 'v_CPVwLlj4Wratw0ZDTt',
          defaultMessage: 'Luxury Escapes Support Assistant',
          description: 'Aria label for chat support in mobile phone menu element',
        })}
        rel="nofollow"
      />}
      {shouldRenderWhatsApp &&
        <ClickableListItem
          title={intl.formatMessage({
            id: 'hkLBlFVBZLq8KFDJAqzc',
            defaultMessage: 'WhatsApp',
            description: 'Title for WhatsApp contact option in mobile navigation menu',
          })}
          startIcon={<img
            src={getSVGImageUrl('whatsapp-logo')}
            width="24"
            height="24"
            alt={intl.formatMessage({
              id: 'ECNjAesoTCTQ49NGJwGa',
              defaultMessage: 'WhatsApp',
              description: 'Alt text for WhatsApp logo in mobile navigation menu',
            })}
          />}
          href={`https://wa.me/${config.WHATS_APP_NUMBER}`}
          aria-label={intl.formatMessage({
            id: 'nIOwOa9rIeS_2PXwTOvn',
            defaultMessage: 'WhatsApp',
            description: 'Accessibility label for WhatsApp contact button in mobile navigation menu',
          })}
          rel="noopener"
          target="_blank"
        />}
      <ClickableListItem
        title={intl.formatMessage({
          id: 'rns_PEP9129UqXsNSVD0',
          defaultMessage: 'International Support',
          description: 'Title for international support contact in mobile phone menu',
        })}
        startIcon={config.BRAND === 'luxuryescapes' ? <LinePhoneIcon /> : undefined}
        subtitle={internationalContact.humanReadable}
        href={`tel:${internationalContact.number}`}
        aria-label={intl.formatMessage({
          id: 'DWNebmRa0Zq0AQptZDPO',
          defaultMessage: 'International Support contact',
          description: 'Aria label for international support contact in mobile phone menu',
        })}
        rel="nofollow"
      />
    </div>
  )
}

function mapStateToProps(state: App.State) {
  return {
    supportAssistantEnabled: isSupportAssistantEnabled(state),
  }
}

export default connect(mapStateToProps)(PhoneNumberMobileMenu)
